import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Bookmark, Plus, Trash2, RefreshCw, X, MapPin, Edit2, Check } from 'lucide-react';

const STORAGE_KEY = 'planer_gantt_saved_scenes';

// Pomocnicze formatowanie daty: "DD.MM HH:MM"
const formatShortDate = (ms) => {
  if (!ms) return '--:--';
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '--:--';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month} ${hours}:${mins}`;
};

const GanttScenes = ({
  scrollWrapperRef,
  minDate,
  scaleFactor,
  setScaleFactor,
  buttonClassName,
  openUpwards = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sceneName, setSceneName] = useState('');
  const [activeSceneId, setActiveSceneId] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  // Stany dla edycji nazwy sceny
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const menuRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const editInputRef = useRef(null);

  // Przechowuje docelowy scroll, który zostanie ustawiony synchronicznie przed odmalowaniem klatki
  const pendingScrollRef = useRef(null);

  // Inicjalizacja listy scen z localStorage
  const [scenes, setScenes] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Zapis do localStorage przy każdej zmianie listy
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scenes));
    } catch (e) {
      console.error('Błąd zapisu scen Gantta:', e);
    }
  }, [scenes]);

  // Zamykanie okienka po kliknięciu poza nim
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
        setEditingSceneId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Automatyczny fokus na input edycji po jej rozpoczęciu
  useEffect(() => {
    if (editingSceneId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingSceneId]);

  // Synchroniczne przewinięcie wykresu w tej samej klatce, w której zmienia się scaleFactor (brak skoku)
  useLayoutEffect(() => {
    if (!pendingScrollRef.current || !scrollWrapperRef.current) return;

    const { scrollLeft, scrollTop } = pendingScrollRef.current;
    scrollWrapperRef.current.scrollLeft = scrollLeft;
    scrollWrapperRef.current.scrollTop = scrollTop;
    pendingScrollRef.current = null;
  }, [scaleFactor, scrollWrapperRef]);

  // Krótkie powiadomienie po wywołaniu sceny skrótem klawiszowym
  const showShortcutToast = useCallback((text) => {
    setToastMessage(text);
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 1600);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Przejście do wybranej sceny (bez setTimeout - natychmiastowo lub przez useLayoutEffect)
  const handleLoadScene = useCallback(
    (scene, fromShortcut = false, index = 0) => {
      const wrapper = scrollWrapperRef.current;
      if (!scene || !wrapper) return;

      setActiveSceneId(scene.id);

      if (fromShortcut) {
        showShortcutToast(`Alt+${index + 1}: ${scene.name}`);
      }

      let targetCenterMinutes = scene.centerMinutes;
      if (minDate && scene.centerTimeMs) {
        targetCenterMinutes = (scene.centerTimeMs - minDate.getTime()) / (1000 * 60);
      }

      const newCenterPx = targetCenterMinutes * scene.scale;
      const targetScrollLeft = Math.max(0, newCenterPx - wrapper.clientWidth / 2);
      const targetScrollTop = scene.scrollTop || 0;

      if (scene.scale === scaleFactor) {
        wrapper.scrollLeft = targetScrollLeft;
        wrapper.scrollTop = targetScrollTop;
      } else {
        pendingScrollRef.current = {
          scrollLeft: targetScrollLeft,
          scrollTop: targetScrollTop,
        };
        setScaleFactor(scene.scale);
      }
    },
    [minDate, scaleFactor, scrollWrapperRef, setScaleFactor, showShortcutToast]
  );

  // Obsługa skrótów klawiszowych Alt + 1..9
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      const tag = e.target?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        e.target?.isContentEditable
      ) {
        return;
      }

      let sceneIndex = -1;
      if (e.code.startsWith('Digit')) {
        const num = parseInt(e.code.replace('Digit', ''), 10);
        if (num >= 1 && num <= 9) sceneIndex = num - 1;
      } else if (e.code.startsWith('Numpad')) {
        const num = parseInt(e.code.replace('Numpad', ''), 10);
        if (num >= 1 && num <= 9) sceneIndex = num - 1;
      }

      if (sceneIndex !== -1 && scenes[sceneIndex]) {
        e.preventDefault();
        e.stopPropagation();
        handleLoadScene(scenes[sceneIndex], true, sceneIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scenes, handleLoadScene]);

  // Pobiera parametry aktualnego widoku z wykresu
  const captureCurrentView = () => {
    const wrapper = scrollWrapperRef.current;
    if (!wrapper) return null;

    const containerWidth = wrapper.clientWidth;
    const startPx = wrapper.scrollLeft;
    const endPx = wrapper.scrollLeft + containerWidth;
    const centerPx = wrapper.scrollLeft + containerWidth / 2;

    const startMinutes = startPx / scaleFactor;
    const endMinutes = endPx / scaleFactor;
    const centerMinutes = centerPx / scaleFactor;

    const minTimeMs = minDate ? minDate.getTime() : null;
    const startTimeMs = minTimeMs ? minTimeMs + startMinutes * 60 * 1000 : null;
    const endTimeMs = minTimeMs ? minTimeMs + endMinutes * 60 * 1000 : null;
    const centerTimeMs = minTimeMs ? minTimeMs + centerMinutes * 60 * 1000 : null;

    const rangeLabel =
      startTimeMs && endTimeMs
        ? `${formatShortDate(startTimeMs)} – ${formatShortDate(endTimeMs)}`
        : `Skala: ${Number(scaleFactor).toFixed(2)}x`;

    return {
      scale: scaleFactor,
      scrollTop: wrapper.scrollTop,
      centerMinutes,
      centerTimeMs,
      rangeLabel,
    };
  };

  // Dodanie nowej sceny
  const handleAddScene = (e) => {
    e?.preventDefault();
    const view = captureCurrentView();
    if (!view) return;

    const trimmedName = sceneName.trim();
    const defaultName = `Scena ${scenes.length + 1}`;

    const newScene = {
      id: Date.now(),
      name: trimmedName || defaultName,
      ...view,
    };

    setScenes((prev) => [...prev, newScene]);
    setActiveSceneId(newScene.id);
    setSceneName('');
  };

  // Nadpisanie istniejącej sceny bieżącym miejscem i powiększeniem
  const handleUpdateScene = (id, e) => {
    e.stopPropagation();
    const view = captureCurrentView();
    if (!view) return;

    setScenes((prev) =>
      prev.map((scene) => (scene.id === id ? { ...scene, ...view } : scene))
    );
    setActiveSceneId(id);
  };

  // Rozpoczęcie edycji nazwy
  const handleStartEdit = (scene, e) => {
    e.stopPropagation();
    setEditingSceneId(scene.id);
    setEditingName(scene.name);
  };

  // Zapis nowej nazwy
  const handleSaveEdit = (id, e) => {
    e?.stopPropagation();
    const trimmed = editingName.trim();
    if (trimmed) {
      setScenes((prev) =>
        prev.map((s) => (s.id === id ? { ...s, name: trimmed } : s))
      );
    }
    setEditingSceneId(null);
  };

  // Usunięcie pojedynczej sceny
  const handleDeleteScene = (id, e) => {
    e.stopPropagation();
    setScenes((prev) => prev.filter((s) => s.id !== id));
    if (activeSceneId === id) setActiveSceneId(null);
    if (editingSceneId === id) setEditingSceneId(null);
  };

  // Wyczyszczenie wszystkich scen
  const handleClearAll = () => {
    setScenes([]);
    setActiveSceneId(null);
    setEditingSceneId(null);
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Dymek potwierdzający przełączenie skrótem Alt + 1..9 */}
      {toastMessage && !isOpen && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            backgroundColor: '#111827',
            color: '#34d399',
            border: '1px solid #374151',
            borderRadius: '5px',
            padding: '4px 10px',
            fontSize: '11px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
            pointerEvents: 'none',
            zIndex: 120,
          }}
        >
          {toastMessage}
        </div>
      )}

      <button
        className={buttonClassName}
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          backgroundColor: scenes.length > 0 ? '#19675e' : '#434343',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
        }}
        title="Zapisane sceny (szybkie przełączanie: Alt + 1..9)"
      >
        <Bookmark size={18} />
        <span>({scenes.length})</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: openUpwards ? 'auto' : '100%',
            bottom: openUpwards ? '100%' : 'auto',
            right: 0,
            marginBottom: openUpwards ? '6px' : 0,
            marginTop: openUpwards ? 0 : '6px',
            width: '340px',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            border: '1px solid #374151',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            zIndex: 110,
            padding: '10px',
          }}
        >
          {/* Nagłówek okienka */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '6px',
              marginBottom: '8px',
              borderBottom: '1px solid #374151',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <span>Zapisane sceny ({scenes.length})</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {scenes.length > 0 && (
                <button
                  onClick={handleClearAll}
                  style={{
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    cursor: 'pointer',
                  }}
                  title="Usuń wszystkie zapisane sceny"
                >
                  Wyczyść
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setEditingSceneId(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  display: 'flex',
                  padding: '2px',
                }}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Formularz dodawania bieżącej sceny */}
          <form
            onSubmit={handleAddScene}
            style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}
          >
            <input
              type="text"
              value={sceneName}
              onChange={(e) => setSceneName(e.target.value)}
              placeholder="Nazwa sceny..."
              style={{
                flex: 1,
                backgroundColor: '#111827',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                color: '#f9fafb',
                padding: '5px 8px',
                fontSize: '11px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              style={{
                backgroundColor: '#2a9d8f',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '5px 10px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
              }}
              title="Zapisz aktualne miejsce i stopień powiększenia"
            >
              <Plus size={14} />
              <span>Zapisz</span>
            </button>
          </form>

          {/* Lista zapisanych scen */}
          {scenes.length === 0 ? (
            <div
              style={{
                fontSize: '11px',
                color: '#9ca3af',
                textAlign: 'center',
                padding: '12px 4px',
              }}
            >
              Brak zapisanych scen. Ustaw widok na wykresie i kliknij „Zapisz”.
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                maxHeight: '240px',
                overflowY: 'auto',
              }}
            >
              {scenes.map((scene, index) => {
                const isActive = scene.id === activeSceneId;
                const isEditing = scene.id === editingSceneId;
                const shortcutLabel = index < 9 ? `Alt+${index + 1}` : null;

                return (
                  <div
                    key={scene.id}
                    onClick={() => {
                      if (!isEditing) handleLoadScene(scene, false, index);
                    }}
                    onDoubleClick={(e) => handleStartEdit(scene, e)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      backgroundColor: isActive ? '#1e3a8a' : '#374151',
                      border: isActive ? '1px solid #60a5fa' : '1px solid transparent',
                      borderRadius: '4px',
                      cursor: isEditing ? 'default' : 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    title={
                      isEditing
                        ? ''
                        : shortcutLabel
                        ? `Kliknij lub wciśnij ${shortcutLabel} (Dwuklik: edytuj nazwę)`
                        : 'Kliknij, aby przejść (Dwuklik: edytuj nazwę)'
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        marginRight: '8px',
                        flex: 1,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#f9fafb',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <MapPin size={12} color="#34d399" style={{ flexShrink: 0 }} />
                        {shortcutLabel && (
                          <span
                            style={{
                              fontSize: '9px',
                              fontWeight: 700,
                              backgroundColor: '#111827',
                              color: '#93c5fd',
                              border: '1px solid #4b5563',
                              borderRadius: '3px',
                              padding: '1px 4px',
                              flexShrink: 0,
                            }}
                          >
                            {shortcutLabel}
                          </span>
                        )}

                        {isEditing ? (
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              ref={editInputRef}
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit(scene.id, e);
                                if (e.key === 'Escape') setEditingSceneId(null);
                              }}
                              style={{
                                width: '100%',
                                backgroundColor: '#111827',
                                border: '1px solid #60a5fa',
                                borderRadius: '3px',
                                color: '#ffffff',
                                padding: '2px 5px',
                                fontSize: '11px',
                                outline: 'none',
                              }}
                            />
                            <button
                              onClick={(e) => handleSaveEdit(scene.id, e)}
                              style={{
                                background: '#10b981',
                                border: 'none',
                                color: '#fff',
                                borderRadius: '3px',
                                padding: '3px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                              title="Zapisz nazwę"
                            >
                              <Check size={12} />
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flex: 1,
                            }}
                          >
                            {scene.name}
                          </span>
                        )}
                      </div>

                      <span
                        style={{
                          fontSize: '10px',
                          color: '#9ca3af',
                          marginTop: '2px',
                        }}
                      >
                        {scene.rangeLabel} • Zoom: {Number(scene.scale).toFixed(2)}x
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      {!isEditing && (
                        <button
                          onClick={(e) => handleStartEdit(scene, e)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#93c5fd',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '3px',
                          }}
                          title="Zmień nazwę sceny"
                        >
                          <Edit2 size={13} />
                        </button>
                      )}

                      <button
                        onClick={(e) => handleUpdateScene(scene.id, e)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#60a5fa',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '3px',
                        }}
                        title="Nadpisz tę scenę aktualnym widokiem"
                      >
                        <RefreshCw size={13} />
                      </button>

                      <button
                        onClick={(e) => handleDeleteScene(scene.id, e)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#f87171',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '3px',
                        }}
                        title="Usuń scenę"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GanttScenes;